// Complete Math & Paper Calculation Engine for ISO 9001 Printing & Packaging ERP

export interface FinishingSpecs {
  maMang?: string;            // Mã màng (OPP bóng, OPP mờ, màng nhiệt...)
  khoMang?: number | string;  // Khổ màng (cm)
  slMangM?: number | string;  // Số lượng màng (m mét)
  dienTichEpKim?: number | string; // Diện tích ép kim (cm²)
  maNhu?: string;             // Mã nhũ (Nhũ vàng 111, Nhũ bạc, Nhũ 7 màu...)
  trangPhu?: string;          // Tráng phủ (UV toàn phần, UV định hình, Vecni...)
  chietQuang?: string;        // Chiết quang
  boi?: string;               // Bồi (Bồi sóng E, Bồi sóng B, Bồi duplex...)
  beNoi?: string;             // Bế nổi / Dập nổi
  kieuBe?: string;            // Kiểu bế (Bế đứt, Bế demi, Bế cửa sổ...)
  soKhuonBe?: number | string; // Số lượng khuôn bế
  khuonBe?: string;           // Mã / tên khuôn bế
  ghiChuBe?: string;          // Ghi chú bế
  xe?: string;                // Xé (Xé cửa sổ, Xé răng cưa...)
  dan?: string;               // Dán (Dán mộc, Dán móc đáy, Dán 4 góc...)
  catThanhPham?: string;      // Cắt thành phẩm
  thanhPhamKhac?: string;     // Thành phẩm khác
  ghiChuThanhPham?: string;   // Ghi chú thành phẩm
  ghiChuBinhBai?: string;     // Ghi chú bình bài (Layout note)
  ghiChuGiaoHang?: string;    // Ghi chú giao hàng
  hinhBaiBinh1?: string;      // URL hình bài bình mặt 1
  hinhBaiBinh2?: string;      // URL hình bài bình mặt 2
}

export interface F0PlanStep {
  cong_doan: string;
  dvt: string;
  sl_yeu_cau: number;       // Số lượng đầu ra (Output) cần đạt
  sl_dat?: number | string;
  sl_hu?: number | string;
  bu_hao: number;           // Bù hao tuyệt đối (Input - Output)
  sl_cap?: number;          // Tùy chọn hiển thị: Số lượng đầu vào (Input) = Yêu cầu + Bù hao
  fixed_waste?: number;     // Ghi nhớ setup cố định (để hiển thị)
  var_waste_percent?: number; // Ghi nhớ % bù hao chạy máy (để hiển thị)
  nguoi_thuc_hien?: string;
  ghi_chu?: string;
}

export interface PaperPlanInputs {
  qtyOrder: number;                 // SL sản phẩm đặt hàng (ví dụ: 10,000 cái)
  pcsPerSheet: number;              // Số con sản phẩm trên 1 tờ in (ví dụ: 4 con/tờ)
  purchasedPaperSize?: string;      // Khổ giấy mua (ví dụ: "65x86" hoặc "65 x 86 cm")
  printingPaperSize?: string;       // Khổ tờ in (ví dụ: "43x65" hoặc "43 x 65 cm")
  customCutRatio?: number;          // Tỷ lệ cắt tùy chỉnh (nếu có)
  totalPlanSheets?: number;         // Tổng số tờ in kế hoạch (có bù hao) được tính từ F0
}

export interface PaperPlanResult {
  netPrintingSheets: number;        // Số tờ in tịnh (không bù hao)
  totalPlanSheets: number;          // Tổng số tờ in kế hoạch (có bù hao)
  cutRatio: number;                 // Tỷ lệ cắt tờ mua -> tờ in
  totalMaterialRequired: number;    // Số lượng tờ giấy mua cần cấp/mua (tờ)
  purchasedReams: number;           // Số lượng Ream giấy mua (1 Ream = 500 tờ)
}

/**
 * Tách chiều rộng & chiều dài từ chuỗi khổ giấy (vd: "65 x 86 cm" -> { width: 65, height: 86 })
 */
export function parsePaperSize(sizeStr?: string): { width: number; height: number } {
  if (!sizeStr) return { width: 0, height: 0 };
  const cleaned = sizeStr.replace(/[^\d.xX*,\s]/g, "").replace(",", ".");
  const parts = cleaned.split(/[xX*,\s]+/).map(p => parseFloat(p)).filter(p => !isNaN(p) && p > 0);
  if (parts.length >= 2) {
    return { width: Math.min(parts[0], parts[1]), height: Math.max(parts[0], parts[1]) };
  }
  return { width: 0, height: 0 };
}

/**
 * Tự động tính toán tỷ lệ cắt từ khổ giấy mua sang khổ tờ in (Cut Ratio)
 */
export function calculateCutRatio(purchasedSize?: string, printingSize?: string, defaultRatio: number = 1): number {
  const buy = parsePaperSize(purchasedSize);
  const print = parsePaperSize(printingSize);

  if (buy.width <= 0 || buy.height <= 0 || print.width <= 0 || print.height <= 0) {
    return defaultRatio > 0 ? defaultRatio : 1;
  }

  // Phương án 1: Cùng chiều (Width/Width * Height/Height)
  const option1 = Math.floor(buy.width / print.width) * Math.floor(buy.height / print.height);
  // Phương án 2: Xoay chiều (Width/Height * Height/Width)
  const option2 = Math.floor(buy.width / print.height) * Math.floor(buy.height / print.width);

  const bestRatio = Math.max(option1, option2);
  return bestRatio > 0 ? bestRatio : defaultRatio;
}

/**
 * Engine Tính Toán Kế Hoạch Giấy / Vật Tư Toàn Diện (ISO 9001 Paper Math)
 */
export function calculatePaperPlan(inputs: PaperPlanInputs): PaperPlanResult {
  const {
    qtyOrder = 0,
    pcsPerSheet = 1,
    purchasedPaperSize = "",
    printingPaperSize = "",
    customCutRatio,
    totalPlanSheets = 0, // Should be passed from generateBackwardKHSX output
  } = inputs;

  const validPcs = pcsPerSheet > 0 ? pcsPerSheet : 1;
  const netPrintingSheets = Math.ceil(qtyOrder / validPcs);

  // If totalPlanSheets is not provided (e.g., initial state), default to netPrintingSheets
  const actualTotalPlanSheets = totalPlanSheets > 0 ? totalPlanSheets : netPrintingSheets;

  const calculatedCut = calculateCutRatio(purchasedPaperSize, printingPaperSize, 1);
  const cutRatio = customCutRatio && customCutRatio > 0 ? customCutRatio : calculatedCut;

  const totalMaterialRequired = Math.ceil(actualTotalPlanSheets / cutRatio);
  const purchasedReams = Number((totalMaterialRequired / 500).toFixed(2));

  return {
    netPrintingSheets,
    totalPlanSheets: actualTotalPlanSheets,
    cutRatio,
    totalMaterialRequired,
    purchasedReams,
  };
}

/**
 * Default waste values for standard printing & packaging processes.
 * fixed: Tờ/Pcs cố định hao hụt khi canh chỉnh máy (make-ready)
 * varPercent: % hao hụt trong quá trình chạy máy (running waste)
 */
const PROCESS_WASTE_DEFAULTS: Record<string, { fixed: number, varPercent: number }> = {
  "IN": { fixed: 100, varPercent: 0.02 },    // In offset
  "CAN": { fixed: 20, varPercent: 0.01 },    // Cán màng
  "BE": { fixed: 30, varPercent: 0.01 },     // Bế
  "DAN": { fixed: 50, varPercent: 0.02 },    // Dán hộp
  "GIUT": { fixed: 10, varPercent: 0.01 },   // Giựt, rọc
  "EP": { fixed: 30, varPercent: 0.01 },     // Ép kim
  "TE": { fixed: 10, varPercent: 0.01 },     // Tề, cắt
  "ZERO": { fixed: 0, varPercent: 0.00 }     // Không có hao hụt (kiểm, đóng gói)
};

/**
 * Thuật toán Tính Bù hao & KHSX Tự động (Backward Allocation ISO 9001)
 * Truy hồi từ SL Giao hàng (O) về Công đoạn đầu tiên.
 */
export function generateBackwardKHSX(
  O: number,                  // Tổng SL sản phẩm đặt hàng (Thành phẩm cuối cùng)
  k: number,                  // Số con trên tờ (Pcs per sheet)
  quyTrinhStr: string,        // Chuỗi quy trình (vd: "In Offset, Cán màng, Bế, Dán thành phẩm")
  nhomSP: string = ""         // Nhóm sản phẩm (Hộp, Túi, Decal, Nhãn)
): F0PlanStep[] {
  const processes = (quyTrinhStr || "").split(/\s*,\s*/).filter(Boolean);
  if (processes.length === 0) return [];

  const validK = k > 0 ? k : 1;
  const plan: F0PlanStep[] = [];

  // 1. Phân loại Unit và Nhóm Công Đoạn
  const parsedSteps = processes.map(name => {
    const lowerName = name.toLowerCase();
    
    // Đơn vị tính
    let unit = "Tờ";
    if (
      lowerName.includes("dán") ||
      lowerName.includes("ép túi") ||
      lowerName.includes("ép 3") ||
      lowerName.includes("ép 4") ||
      lowerName.includes("ép gia") ||
      lowerName.includes("kiểm") ||
      lowerName.includes("đóng") ||
      lowerName.includes("giao") ||
      lowerName.includes("xé hộp")
    ) {
      unit = "pcs";
    } else if (lowerName.includes("chia cuộn")) {
      unit = "m";
    } else if (lowerName.includes("in offset cuộn")) {
      unit = "m2";
    }

    // Nhóm để lấy định mức hao hụt
    let group = "ZERO";
    if (lowerName.includes("kiểm") || lowerName.includes("đóng") || lowerName.includes("giao")) group = "ZERO";
    else if (lowerName.includes("xé hộp") || lowerName.includes("ép kim") || lowerName.includes("bồi") || lowerName.includes("ép 3 biên")) group = "ZERO";
    else if (lowerName.includes("in")) group = "IN";
    else if (
      lowerName.includes("cán") ||
      lowerName.includes("tráng phủ") ||
      lowerName.includes("uv") ||
      lowerName.includes("vecni")
    ) group = "CAN";
    else if (lowerName.includes("giựt") || lowerName.includes("rọc")) group = "GIUT";
    else if (lowerName.includes("bế")) group = "BE";
    else if (lowerName.includes("dán")) group = "DAN";
    else if (lowerName.includes("ép")) group = "EP";
    else if (lowerName.includes("tề")) group = "TE";
    else if (lowerName.includes("cắt")) group = "TE"; // Cắt thành phẩm thường ít hao

    const defaults = PROCESS_WASTE_DEFAULTS[group] || PROCESS_WASTE_DEFAULTS["ZERO"];

    return {
      name,
      unit,
      fixedWaste: defaults.fixed,
      varPercent: defaults.varPercent
    };
  });

  // 2. Tính toán Truy hồi (Backward Loop)
  let currentQty = O;
  let currentUnit = "pcs";

  // Duyệt mảng ngược từ cuối lên đầu
  for (let i = parsedSteps.length - 1; i >= 0; i--) {
    const step = parsedSteps[i];

    // Chuyển đổi Đơn vị nếu cần (từ Pcs sang Tờ) khi đi lùi
    if (step.unit === "Tờ" && currentUnit === "pcs") {
      currentQty = Math.ceil(currentQty / validK);
      currentUnit = "Tờ";
    } 
    // Ghi chú: Nếu hệ thống có cuộn (m/m2) thì có thể cần conversion logic tương ứng, 
    // hiện tại tập trung Tờ/Pcs.

    const outputRequired = currentQty;

    // Tính Input = Output + Bù hao
    // Công thức (đồng nhất với PricingService backend): Input = Math.ceil(Output / (1 - Var%)) + Fixed
    // Setup waste (Fixed) là số lượng bù hao cố định, không chịu hao hụt chạy máy (Var%).
    let inputNeeded = outputRequired;
    if (step.fixedWaste > 0 || step.varPercent > 0) {
      inputNeeded = Math.ceil(outputRequired / (1 - step.varPercent)) + step.fixedWaste;
    }

    const waste = Math.max(0, inputNeeded - outputRequired);

    // Lưu vào plan (sẽ push ngược mảng nên cần unshift hoặc push rồi reverse)
    plan.unshift({
      cong_doan: step.name,
      dvt: step.unit,
      sl_yeu_cau: outputRequired,  // Số lượng RA đạt tiêu chuẩn
      sl_cap: inputNeeded,         // Số lượng VÀO
      bu_hao: waste,
      fixed_waste: step.fixedWaste,
      var_waste_percent: step.varPercent,
      nguoi_thuc_hien: "",
      ghi_chu: ""
    });

    // Cập nhật currentQty cho bước lùi tiếp theo (Bước lùi cần Input của bước hiện tại làm Output)
    currentQty = inputNeeded;
  }

  return plan;
}
